import { useEffect, useState } from "react";
import { useFetchPreview } from "../services/api";
import { readEnvelope } from "../services/envelope";

/* Long enough that typing a count or a time does not fire a preview per keystroke.
   The endpoint reads no database, so this is about request volume, not its cost. */
const DEBOUNCE_MS = 500;

/**
 * The backend's answer to every date and count the screens show (E3).
 *
 * Both screens held eight derivations of their own before this — the projected
 * completion, the flex window, a successor's start, the validity comparison — and
 * each was a second opinion the backend could disagree with at the write. They are
 * all one call now, and the screen displays what it answers.
 *
 * The answer is dropped the moment the request changes, so a figure on screen is
 * never one the current schedule did not produce. While it is missing the screens
 * say so rather than falling back to a stored value: `isPending` is what a save gate
 * reads. A refusal is kept as its message and the answer stays null.
 *
 * @param {Object|null} request - the E3 body
 * @param {boolean} [enabled] - false while it cannot be asked yet: an incomplete
 *   schedule, no course chosen, a count not yet typed
 * @returns {{ preview: Object|null, isPending: boolean, error: string }}
 */
export const usePreview = (request, enabled = true) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const { mutate: fetchPreview } = useFetchPreview();

  /* The body itself is the identity: two schedules that serialise the same need no
     second answer, and any real edit is a different string. */
  const key = enabled && request ? JSON.stringify(request) : "";

  useEffect(() => {
    setPreview(null);
    setError("");
    if (!key) return undefined;

    let isCurrent = true;
    const timer = setTimeout(async () => {
      try {
        const answer = await fetchPreview({ data: JSON.parse(key) });
        if (isCurrent) setPreview(answer ?? null);
      } catch (thrown) {
        if (isCurrent) setError(readEnvelope(thrown)?.message);
      }
    }, DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    preview,
    /* Asked for, not yet answered — and a refusal is not pending, it is answered. */
    isPending: !!key && !preview && !error,
    error,
  };
};

export default usePreview;
